import React, { useState, useMemo } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { 
  getColumnsByCategory, 
  ColumnCategory,
  COLUMN_DEFINITIONS 
} from '../../utils/exportColumns'

interface ColumnSelectorProps {
  selectedColumns: string[]
  onToggleColumn: (key: string) => void
  onSelectAll: () => void
  onDeselectAll: () => void
}

const CATEGORY_INFO: Record<ColumnCategory, { label: string; icon: string }> = {
  pedido: { label: 'Pedido', icon: '📦' },
  cliente: { label: 'Cliente', icon: '👤' },
  producto: { label: 'Producto', icon: '🛍️' },
  envio: { label: 'Envío', icon: '🚚' },
  pago: { label: 'Pago', icon: '💳' },
  tienda: { label: 'Tienda', icon: '🏪' },
  agente: { label: 'Agente', icon: '👨‍💼' }
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  selectedColumns,
  onToggleColumn,
  onSelectAll,
  onDeselectAll
}) => {
  const [collapsedCategories, setCollapsedCategories] = useState<Set<ColumnCategory>>(
    new Set<ColumnCategory>(['cliente', 'producto', 'envio', 'pago', 'tienda', 'agente'] as ColumnCategory[]) // Solo 'pedido' expandido por defecto
  )
  
  const columnsByCategory = useMemo(() => getColumnsByCategory(), [])
  
  const toggleCategory = (category: ColumnCategory) => {
    setCollapsedCategories(prev => {
      const newSet = new Set(prev)
      
      if (newSet.has(category)) {
        // Si está colapsado, expandirlo
        newSet.delete(category)
      } else {
        // Si está expandido, colapsarlo
        newSet.add(category)
      }
      
      return newSet
    })
  }
  
  const isCategoryCollapsed = (category: ColumnCategory) => {
    return collapsedCategories.has(category)
  }
  
  const getSelectedCount = (category: ColumnCategory) => {
    return columnsByCategory[category].filter(col => 
      selectedColumns.includes(col.key)
    ).length
  }
  
  const allSelected = selectedColumns.length === COLUMN_DEFINITIONS.length
  const noneSelected = selectedColumns.length === 0
  
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-gray-200">
      {/* Header compacto */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700">Columnas a Exportar</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onSelectAll}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
            disabled={allSelected}
          >
            Todo
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={onDeselectAll}
            className="text-xs text-gray-600 hover:text-gray-700 font-medium px-2 py-1 rounded hover:bg-gray-50 transition-colors"
            disabled={noneSelected}
          >
            Ninguno
          </button>
        </div>
      </div>
      
      {/* Acordeones por categoría - 2 columnas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 items-start">
        {(Object.keys(columnsByCategory) as ColumnCategory[]).map(category => {
          const categoryColumns = columnsByCategory[category]
          const selectedCount = getSelectedCount(category)
          const isCollapsed = isCategoryCollapsed(category)
          const categoryInfo = CATEGORY_INFO[category]
          
          if (categoryColumns.length === 0) return null
          
          return (
            <div 
              key={category} 
              className="border border-gray-200 rounded-md overflow-hidden"
              style={{ height: isCollapsed ? 'auto' : 'auto' }}
            >
              {/* Header del acordeón */}
              <button
                type="button"
                onClick={() => toggleCategory(category)}
                className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm">{categoryInfo.icon}</span>
                  <span className="text-xs font-medium text-gray-700">
                    {categoryInfo.label}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({selectedCount}/{categoryColumns.length})
                  </span>
                </div>
                <ChevronDownIcon
                  className={`h-4 w-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${
                    isCollapsed ? 'transform rotate-180' : ''
                  }`}
                />
              </button>
              
              {/* Contenido del acordeón - Solo renderizar si no está colapsado */}
              {!isCollapsed && (
                <div className="p-2 bg-white">
                  <div className="grid grid-cols-2 gap-2">
                    {categoryColumns.map(column => {
                      const isSelected = selectedColumns.includes(column.key)
                      return (
                        <label
                          key={column.key}
                          className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-50 p-1.5 rounded text-xs"
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => onToggleColumn(column.key)}
                            className="h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-1"
                          />
                          <span className="text-gray-700 truncate" title={column.label}>
                            {column.label}
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
      
      {/* Footer con resumen */}
      <div className="mt-3 pt-2 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          {selectedColumns.length} de {COLUMN_DEFINITIONS.length} columnas seleccionadas
        </p>
      </div>
    </div>
  )
}

export default React.memo(ColumnSelector)

